// ---------------------------------------------------------------------------
// V31 — THE PRIVACY CENTRE. `console.privacy`, at `privacy-centre`.
//
// The path is `privacy-centre` rather than `privacy` for the reason
// `console.compliance` is at `compliance-centre` and `console.workers` is at
// `workers`: V5's guard reads a file full of bare vocabulary words in the browser
// as a taxonomy, which is absolute rule 4's second source of truth. `privacy` is
// not an `app.org_role` today — measured, not assumed — but the convention is
// already in the file and following it costs nothing.
//
// ---------------------------------------------------------------------------
// FOUR QUESTIONS, IN THE ORDER SOMEBODY UNDER PRESSURE ASKS THEM
//
//   1. Who has asked for something, and when does the clock run out. The deadline
//      is computed in the database from the statutory period the vocabulary
//      carries, so this screen renders a number rather than deciding one.
//   2. Who cannot be erased right now, and why. A live legal hold is the one
//      thing that turns "yes, of course" into "no, and here is what would have to
//      happen first" — so it is on the same screen as the request, not filed
//      under settings.
//   3. What was actually erased. The evidence row, which holds no name.
//   4. What this product keeps, for how long, and on what basis — with the
//      coverage defects rendered rather than hidden, because a schedule that
//      does not cover a table is exactly the state nobody would look for.
//
// The working-time limits sit at the bottom under their own heading. They are
// the other half of the slice and they are genuinely a different subject; what
// makes them belong on a compliance surface is that both answer the same
// question — what does the law where we operate require of us — and neither has
// anywhere else to be read.
//
// ---------------------------------------------------------------------------
// THE ERASURE BUTTON IS NOT GUARDED BY A CONFIRMATION DIALOG
//
// It is guarded by a recorded request it has to point at, a permission, and a
// legal-hold gate that refuses in the database. A browser `confirm()` would add
// nothing any of those do not already do, and this product's sibling learned that
// a native `confirm` is suppressed in some surfaces anyway. What IS here is the
// count of live holds beside the button, so the refusal is legible before it
// happens rather than only after.
//
// NO NEW CSS. Every class used here already carries a rule in `web/src/design` —
// `stack`, `panel`, `data-table`, `muted`, `btn`, `field`, `row`, `chip`. V30's
// screen shipped a shell class with no rule anywhere and rendered as unstyled
// markup; the check is a grep, and it takes a minute.
//
// AND THE CLASS NAME IS NOT WRITTEN OUT IN THIS COMMENT EITHER. The first draft
// of this header quoted the offending attribute verbatim to explain it, and
// `verify-design-parity.sh` failed on the quotation: it scans source without
// stripping comments, so a class named in prose is a class named in the file.
// Same shape as V30's *never quote a vocabulary value in prose*.
// ---------------------------------------------------------------------------

import { useEffect, useState } from "react";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import {
  privacy as privacyApi,
  type PrivacyOverview,
  type PrivacyPerson,
  type PrivacyVocabulary,
} from "../api/privacy";
import { Notice } from "../design/Notice";
import { AddButton } from "../design/AddLink";
import { LoadFailed } from "../design/LoadFailed";
import { StatusChip } from "../design/StatusChip";
import { Tabs } from "../design/Tabs";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
import { whenIn } from "../system/format";

type WorkerLite = { id: string; given_name: string; family_name: string };

// ---------------------------------------------------------------------------
// ★ ONE PICKER, TWO KINDS OF PERSON, AND THE VALUE CARRIES THE KIND.
//
// D13 put agency staff and a client's portal users in scope for Article 15 and
// 17. Two separate `<select>`s would let somebody fill in both and leave the
// screen to decide which they meant; one control whose value is `kind:id` cannot
// express that at all, which is the same reason the Worker refuses a body naming
// two subjects rather than picking one.
//
// The two groups are `<optgroup>`s so that a person who exists on the bench AND
// as a login is visibly two entries under two headings, rather than one row a
// compliance officer has to guess the meaning of.
// ---------------------------------------------------------------------------
type SubjectRef = { kind: "worker" | "account"; id: string };

/** The kind's label comes from `app.subject_kind`, never from a literal here —
 *  absolute rule 4, the same reason no request kind or hold reason is spelled in
 *  this file. An id with no row falls back to the id so a missing vocabulary row
 *  is visible rather than blank. */
function subjectKindLabel(data: PrivacyOverview, id: string): string {
  return data.subject_kinds.find((k) => k.id === id)?.label ?? id;
}

function parseSubject(value: string): SubjectRef | null {
  const at = value.indexOf(":");
  if (at < 1) return null;
  const kind = value.slice(0, at);
  const id = value.slice(at + 1);
  if (id === "" || (kind !== "worker" && kind !== "account")) return null;
  return { kind, id };
}

function SubjectPicker({
  label,
  value,
  onChange,
  people,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  people: PrivacyPerson[];
}) {
  // SWEEP S15. THE BENCH IS ASKED WITH WHAT IS TYPED. It loaded the first 50 people and
  // stopped, so a privacy request could not be opened for anybody further down the bench.
  const { apiBase } = useSession();
  const { t } = useT();
  const [q, setQ] = useState("");
  const [workers, setWorkers] = useState<WorkerLite[]>([]);
  const [kept, setKept] = useState<WorkerLite | null>(null);
  useEffect(() => {
    const t = window.setTimeout(() => {
      send<{ workers?: WorkerLite[] }>(console_.listWorkers(apiBase, { q: q.trim(), limit: 20 }))
        .then((res) => setWorkers(res.status === 200 ? (res.body.workers ?? []) : []))
        .catch(() => setWorkers([]));
    }, 250);
    return () => window.clearTimeout(t);
  }, [apiBase, q]);
  const shown = kept && !workers.some((w) => w.id === kept.id) ? [kept, ...workers] : workers;
  return (
    <label className="field subject-picker">
      {label}
      <input type="search" aria-label={t("Search the bench: {label}", { label })} placeholder={t("Type a name")} value={q} onChange={(e) => setQ(e.target.value)} />
      {/* A <label> names only its FIRST control, which is the search box above. */}
      <select
        aria-label={label}
        value={value}
        onChange={(e) => {
          const w = shown.find((x) => `worker:${x.id}` === e.target.value);
          if (w) setKept(w);
          onChange(e.target.value);
        }}
      >
        <option value="">{t("Choose a person")}</option>
        <optgroup label={t("On the bench")}>
          {shown.map((w) => (
            <option key={`worker:${w.id}`} value={`worker:${w.id}`}>
              {w.given_name} {w.family_name}
            </option>
          ))}
        </optgroup>
        <optgroup label={t("Signs in")}>
          {people.map((pp) => (
            <option key={`account:${pp.account_id}`} value={`account:${pp.account_id}`}>
              {pp.email} · {pp.role}
            </option>
          ))}
        </optgroup>
      </select>
    </label>
  );
}

export function Privacy() {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const tl = (label: string) => translateLabel(locale, label);

  // THE ORGANISATION'S CLOCK, not the reader's. A statutory deadline read at
  // 23:00 in one country about a request received at 08:00 in another is the
  // ordinary case for an agency working across a border.
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;

  const [data, setData] = useState<PrivacyOverview | null>(null);
  const [failed, setFailed] = useState<Failure | null>(null);
  const [vocab, setVocab] = useState<PrivacyVocabulary | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [recordingRequest, setRecordingRequest] = useState(false);
  const [openingHold, setOpeningHold] = useState(false);
  const [reqWorker, setReqWorker] = useState("");
  const [reqKind, setReqKind] = useState("");
  const [holdWorker, setHoldWorker] = useState("");
  const [holdReason, setHoldReason] = useState("");
  const [holdRef, setHoldRef] = useState("");
  const [overrideFor, setOverrideFor] = useState<string | null>(null);
  const [overrideGround, setOverrideGround] = useState("");
  // 0134. The request being closed, and — only when refusing — the ground.
  const [closeFor, setCloseFor] = useState<string | null>(null);
  const [closeGround, setCloseGround] = useState("");
  // 0136. The correction a rectification is being closed against — the same
  // shape as the refusal above, and for the same reason: it appears against the
  // request it concerns, so there is no way to answer one in the abstract.
  const [correctFor, setCorrectFor] = useState<string | null>(null);
  const [correctionId, setCorrectionId] = useState("");

  async function load() {
    const res = await send<PrivacyOverview & { error?: { message: string } }>(
      privacyApi.overview(apiBase),
    );
    if (res.status === 200 && Array.isArray(res.body?.requests)) {
      setData(res.body);
      setFailed(null);
    } else {
      setData(null);
      setFailed({ status: res.status, refusal: refusal(res.body?.error) });
    }
  }

  useEffect(() => {
    void load();
    void (async () => {
      const res = await send<PrivacyVocabulary & { error?: { message: string } }>(
        privacyApi.vocabulary(apiBase),
      );
      if (res.status === 200 && Array.isArray(res.body?.request_kinds)) setVocab(res.body);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  async function act(spec: ReturnType<typeof privacyApi.erase>, done: string) {
    const res = await send<{ error?: { message: string } }>(spec);
    if (res.status >= 200 && res.status < 300) {
      setMessage(done);
      await load();
    } else {
      setMessage(refusal(res.body?.error) ?? t("That did not go through."));
    }
  }

  const liveHolds = (data?.holds ?? []).filter((h) => h.is_live);

  return (
    <div className="stack">
      <h1>{t("Privacy")}</h1>
      <p className="muted">
        {t("What people have asked us to do with their data, what we may not delete yet and why, what we keep and for how long, and the working-time limits that apply where this organisation operates.")}
      </p>

      {failed !== null ? (
        <section className="panel">
          <LoadFailed failure={failed} fallback={t("The privacy centre could not be read.")} onRetry={() => void load()} />
        </section>
      ) : null}

      {message !== null ? (
        <p className="form-message" role="status">
          {message}
        </p>
      ) : null}

      {data !== null ? (
        <Tabs
          label={t("Privacy")}
          tabs={[
            { id: "requests", label: t("Requests"), content: (
<>
          {/* ---- 1. REQUESTS ------------------------------------------- */}
          <section className="panel">
            <div className="panel-head">
              <h2>{t("Requests")}</h2>
              {vocab !== null ? (
                <AddButton label={t("Record a request")} expanded={recordingRequest} onClick={() => setRecordingRequest(!recordingRequest)} />
              ) : null}
            </div>
            {data.requests.length === 0 ? (
              <p className="muted">
                {t("Nobody has asked for anything. That is the ordinary state and not a gap — the obligation is to answer within the statutory period when somebody does, and the clock only starts then.")}
              </p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">{t("Person")}</th>
                    <th scope="col">{t("Asked for")}</th>
                    <th scope="col">{t("State")}</th>
                    <th scope="col">{t("Due")}</th>
                    <th scope="col">{""}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.requests.map((r) => (
                    <tr key={r.id}>
                      <td>
                        {r.subject_name}
                        <br />
                        <span className="muted">
                          {r.opened_by_worker ? t("Asked for it themselves") : t("Recorded on their behalf")}
                        </span>
                      </td>
                      <td>
                        {tl(r.kind_label)}
                        <br />
                        <span className="muted">{r.article}</span>
                      </td>
                      <td>
                        <StatusChip
                          tone={
                            r.tone === "live"
                              ? "live"
                              : r.tone === "failed"
                                ? "failed"
                                : r.tone === "attention"
                                  ? "attention"
                                  : "neutral"
                          }
                          label={tl(r.state_label)}
                        />
                      </td>
                      <td>
                        {whenIn(r.due_at, orgZone)}
                        <br />
                        {/* NEVER COLOUR ALONE. The chip carries the sentence. */}
                        {r.is_terminal ? (
                          <span className="muted">{t("Closed")}</span>
                        ) : (
                          <StatusChip
                            tone={r.days_left < 0 ? "failed" : r.days_left <= 7 ? "attention" : "neutral"}
                            label={
                              r.days_left < 0
                                ? t("{n} day(s) overdue", { n: -r.days_left })
                                : t("{n} day(s) left", { n: r.days_left })
                            }
                          />
                        )}
                      </td>
                      <td>
                        {r.is_erasure && !r.is_terminal ? (
                          <>
                            {r.live_holds > r.overrides ? (
                              <StatusChip
                                tone="attention"
                                label={t("{n} live legal hold(s) — erasure will be refused", { n: r.live_holds - r.overrides })}
                              />
                            ) : null}
                            <button
                              type="button"
                              className="btn"
                              onClick={() =>
                                void act(privacyApi.erase(apiBase, r.id), t("The person was erased."))
                              }
                            >
                              {t("Erase this person")}
                            </button>
                            {r.live_holds > r.overrides ? (
                              <button
                                type="button"
                                className="btn btn-quiet"
                                onClick={() => setOverrideFor(overrideFor === r.id ? null : r.id)}
                              >
                                {overrideFor === r.id ? t("Cancel") : t("Override a hold")}
                              </button>
                            ) : null}
                          </>
                        ) : null}
                        <button
                          type="button"
                          className="btn btn-quiet"
                          onClick={() =>
                            void act(
                              // S70. The subject is whichever id the row carries
                              // (a CHECK makes it exactly one); no kind is compared.
                              r.worker_id !== null
                                ? privacyApi.exportWorker(apiBase, r.worker_id)
                                : privacyApi.exportAccount(apiBase, r.account_id ?? ""),
                              t("The export was produced, and the fact that you took it is recorded."),
                            )
                          }
                        >
                          {t("Export their data")}
                        </button>
                        {/* ★ CLOSING IT — 0134. Before this the only request that
                            could reach a terminal state was an erasure, so an
                            access request stayed open after the export ran and
                            four rights could never be closed at all.

                            `Mark as answered` IS NOT OFFERED FOR AN ERASURE.
                            The database refuses it — marking one done without
                            erasing anybody defeats every proof 0089 built — and
                            hiding the button as well means a compliance officer
                            never presses something that always fails. Refusing
                            an erasure is lawful and stays offered. */}
                        {!r.is_terminal ? (
                          <>
                            {/* ★ AND A CORRECTION IS NOT MARKED DONE BY HAND
                                EITHER — 0136, the paragraph above said about
                                Article 16. A plain `Mark as answered` on a
                                rectification always fails now, so offering it
                                would teach a compliance officer to avoid the
                                control rather than to use it. It opens the
                                picker instead: the correction is chosen from
                                the edits this organisation actually made to
                                this person's record, which the database
                                selects and the screen only renders. */}
                            {r.is_correction ? (
                              <button
                                type="button"
                                className="btn btn-quiet"
                                onClick={() => {
                                  setCorrectionId("");
                                  setCorrectFor(correctFor === r.id ? null : r.id);
                                }}
                              >
                                {correctFor === r.id ? t("Cancel") : t("Record the correction")}
                              </button>
                            ) : !r.is_erasure ? (
                              <button
                                type="button"
                                className="btn btn-quiet"
                                onClick={() =>
                                  void act(
                                    privacyApi.closeRequest(apiBase, r.id, "fulfilled"),
                                    t("The request is recorded as answered and the clock has stopped."),
                                  )
                                }
                              >
                                {t("Mark as answered")}
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="btn btn-quiet"
                              onClick={() => setCloseFor(closeFor === r.id ? null : r.id)}
                            >
                              {closeFor === r.id ? t("Cancel") : t("Refuse it")}
                            </button>
                          </>
                        ) : r.refusal_label ? (
                          <span className="muted">{t("Refused — {ground}", { ground: tl(r.refusal_label) })}</span>
                        ) : r.rectified_by !== null ? (
                          // 0132's ruling: a citation that renders as a row id
                          // informs nobody, so it says what changed.
                          <span className="muted">
                            {r.rectified_fields && r.rectified_fields.length > 0
                              ? t("Corrected — {fields}", { fields: r.rectified_fields.join(", ") })
                              : t("Corrected")}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* THE REFUSAL FORM. It appears only against the request it
                concerns, so there is no way to refuse one in the abstract — the
                override form's shape, for the same reason. The grounds come from
                the database: Article 12(4) obliges this organisation to say why,
                and a list written into this component would go on offering the
                old one the day somebody adds a ground. */}
            {closeFor !== null && vocab !== null ? (
              <div className="stack">
                <h3>{t("Refuse this request")}</h3>
                <label className="field">
                  {t("On what ground")}
                  <select value={closeGround} onChange={(e) => setCloseGround(e.target.value)}>
                    <option value="">{t("Choose a ground")}</option>
                    {vocab.refusal_grounds.map((g) => (
                      <option key={g.id} value={g.id}>
                        {tl(g.label)} · {g.article}
                      </option>
                    ))}
                  </select>
                </label>
                <p className="muted">
                  {(() => {
                    const description = vocab.refusal_grounds.find((g) => g.id === closeGround)?.description;
                    return description
                      ? tl(description)
                      : t("The person has to be told why, so the ground is recorded with the refusal.");
                  })()}
                </p>
                <button
                  type="button"
                  className="btn"
                  disabled={closeGround === ""}
                  onClick={() =>
                    void act(
                      privacyApi.closeRequest(apiBase, closeFor, "refused", closeGround),
                      t("The refusal is recorded against your name, with its ground."),
                    )
                  }
                >
                  {t("Record the refusal")}
                </button>
              </div>
            ) : null}

            {/* ★ THE CORRECTION FORM — 0136. The refusal form's shape, one right
                along, and for the same three reasons: it appears only against
                the request it concerns, the choices come from the database, and
                the control cannot be pressed into a state the database refuses.

                THE EMPTY CASE IS THE ONE THAT MATTERS. A rectification arrives
                before anybody has edited the record, so an empty dropdown is the
                ORDINARY first state of this form — not an error, and not
                something to hide. It says what has to happen first. A form that
                rendered an empty `select` here would read as a broken screen and
                send a compliance officer looking for a defect in the product
                instead of to the worker's record. */}
            {correctFor !== null && data !== null
              ? (() => {
                  const req = data.requests.find((r) => r.id === correctFor);
                  // ★ AND IT CLOSES ITSELF WHEN THERE IS NOTHING LEFT TO ANSWER.
                  // A form still open over a request that has since been closed
                  // — by this act, or by somebody else while this tab sat there
                  // — would fall through to the empty case and tell a compliance
                  // officer that no edit has been made, about a request that was
                  // just fulfilled by one. Reading the state back rather than
                  // clearing it in a callback also covers the second case, which
                  // no callback here could see.
                  if (req === undefined || req.is_terminal) return null;
                  const options = req.corrections ?? [];
                  return (
                    <div className="stack">
                      <h3>{t("Record the correction that answered this")}</h3>
                      {options.length === 0 ? (
                        <p className="muted">
                          {t("No edit has been made to {name}’s record yet. Article 16 is served by changing the data, so put it right on their record first — the change will appear here, and this request closes against it.", { name: req.subject_name })}
                        </p>
                      ) : (
                        <>
                          <label className="field">
                            {t("Which change answered it")}
                            <select
                              value={correctionId}
                              onChange={(e) => setCorrectionId(e.target.value)}
                            >
                              <option value="">{t("Choose the change")}</option>
                              {options.map((c) => (
                                <option key={c.id} value={String(c.id)}>
                                  {whenIn(c.occurred_at, orgZone)} · {c.fields.join(", ")}
                                </option>
                              ))}
                            </select>
                          </label>
                          <p className="muted">
                            {t("Only edits to this person’s record are listed, and only ones that changed something. The request will cite the one you choose, so what was put right stays readable long after the clock has stopped.")}
                          </p>
                          <button
                            type="button"
                            className="btn"
                            disabled={correctionId === ""}
                            onClick={() =>
                              void act(
                                privacyApi.closeRequest(
                                  apiBase,
                                  correctFor,
                                  "fulfilled",
                                  undefined,
                                  Number(correctionId),
                                ),
                                t("The correction is recorded against the request, and the clock has stopped."),
                              )
                            }
                          >
                            {t("Record the correction")}
                          </button>
                        </>
                      )}
                    </div>
                  );
                })()
              : null}

            {/* THE OVERRIDE FORM. One hold at a time, by name, with a ground from
                the list — see routes/privacy.ts. It appears only against the
                request it concerns, so there is no way to authorise an override in
                the abstract. */}
            {overrideFor !== null && vocab !== null ? (
              <div className="stack">
                <h3>{t("Authorise erasure despite a hold")}</h3>
                <p className="muted">
                  {t("This is recorded against your name and survives the erasure it allows. One hold at a time.")}
                </p>
                <label className="field">
                  {t("Ground")}
                  <select
                    value={overrideGround}
                    onChange={(e) => setOverrideGround(e.target.value)}
                  >
                    <option value="">{t("Choose a ground")}</option>
                    {vocab.override_grounds.map((g) => (
                      <option key={g.id} value={g.id}>
                        {tl(g.label)}
                      </option>
                    ))}
                  </select>
                </label>
                {/* ★ THE SAME PERSON, COMPARED AS A SUBJECT AND NOT AS A COLUMN.
                    The obvious filter is `h.worker_id === r.worker_id`, and for an
                    account request both sides are `null` — `null === null` is TRUE
                    in JavaScript, so EVERY live account hold would be offered here,
                    including holds against other people. `app.authorise_erasure_
                    override` refuses those (0123 §8), so nothing could be authorised
                    wrongly; the screen would simply be offering acts that always
                    fail. Comparing the kind first makes the null unreachable. */}
                {liveHolds
                  .filter((h) => {
                    const r = data.requests.find((q) => q.id === overrideFor);
                    if (r === undefined || h.subject_kind !== r.subject_kind) return false;
                    return (
                      (h.worker_id !== null && h.worker_id === r.worker_id) ||
                      (h.account_id !== null && h.account_id === r.account_id)
                    );
                  })
                  .map((h) => (
                    <div className="row" key={h.id}>
                      <span>
                        {tl(h.reason_label)} · {h.reference}
                      </span>
                      <button
                        type="button"
                        className="btn"
                        disabled={overrideGround === ""}
                        onClick={() =>
                          void act(
                            privacyApi.authoriseOverride(apiBase, overrideFor, h.id, overrideGround),
                            t("The override is recorded against your name."),
                          )
                        }
                      >
                        {t("Override this hold")}
                      </button>
                    </div>
                  ))}
              </div>
            ) : null}

            {/* RECORDING A REQUEST THAT ARRIVED ON PAPER. Most requests arrive by
                email or in person, and a product that could only record the ones
                made through its own console would under-count the obligation. */}
            {vocab !== null && recordingRequest ? (
              <div className="stack">
                <div className="form-grid">
                <SubjectPicker
                  label={t("Person")}
                  value={reqWorker}
                  onChange={setReqWorker}
                  people={data.people}
                />
                <label className="field">
                  {t("What they asked for")}
                  <select value={reqKind} onChange={(e) => setReqKind(e.target.value)}>
                    <option value="">{t("Choose")}</option>
                    {vocab.request_kinds.map((k) => (
                      <option key={k.id} value={k.id}>
                        {tl(k.label)} · {k.article} · {t("{n} days", { n: k.statutory_days })}
                      </option>
                    ))}
                  </select>
                </label>
                </div>
                <div className="row">
                <button
                  type="button"
                  className="btn"
                  disabled={parseSubject(reqWorker) === null || reqKind === ""}
                  onClick={() =>
                    void act(
                      privacyApi.openRequest(apiBase, parseSubject(reqWorker)!, reqKind),
                      t("The request is recorded and the clock has started."),
                    )
                  }
                >
                  {t("Record it")}
                </button>
                <button type="button" className="btn btn-quiet" onClick={() => setRecordingRequest(false)}>
                  {t("Cancel")}
                </button>
                </div>
              </div>
            ) : null}
          </section>

</>
) },
            { id: "holds", label: t("Legal holds"), content: (
<>
          {/* ---- 2. LEGAL HOLDS ---------------------------------------- */}
          <section className="panel">
            <div className="panel-head">
              <h2>{t("Legal holds")}</h2>
              {vocab !== null ? (
                <AddButton label={t("Open a hold")} expanded={openingHold} onClick={() => setOpeningHold(!openingHold)} />
              ) : null}
            </div>
            <p className="muted">
              {t("A hold stops an erasure. It is never edited and never deleted: releasing one leaves the row standing with the date it ended, because the record of why data was kept is the first thing anybody asks about afterwards.")}
            </p>
            {data.holds.length === 0 ? (
              <p className="muted">
                {t("No holds, live or released. Nothing here is stopping an erasure.")}
              </p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">{t("Person")}</th>
                    <th scope="col">{t("Reason")}</th>
                    <th scope="col">{t("Case")}</th>
                    <th scope="col">{t("State")}</th>
                    <th scope="col">{""}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.holds.map((h) => (
                    <tr key={h.id}>
                      <td>{h.subject_name}</td>
                      <td>{h.reason_label}</td>
                      <td>{h.reference}</td>
                      <td>
                        <StatusChip
                          tone={h.is_live ? "attention" : "neutral"}
                          label={
                            h.is_live
                              ? t("Live since {when}", { when: whenIn(h.opened_at, orgZone) })
                              : t("Released {when}", { when: whenIn(h.released_at ?? h.opened_at, orgZone) })
                          }
                        />
                      </td>
                      <td>
                        {h.is_live ? (
                          <button
                            type="button"
                            className="btn"
                            onClick={() =>
                              void act(
                                privacyApi.releaseHold(apiBase, h.id),
                                t("The hold is released. The row stays."),
                              )
                            }
                          >
                            {t("Release")}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {vocab !== null && openingHold ? (
              <div className="stack">
                <div className="form-grid">
                <SubjectPicker
                  label={t("Person")}
                  value={holdWorker}
                  onChange={setHoldWorker}
                  people={data.people}
                />
                <label className="field">
                  {t("Reason")}
                  <select value={holdReason} onChange={(e) => setHoldReason(e.target.value)}>
                    <option value="">{t("Choose a reason")}</option>
                    {vocab.hold_reasons.map((r) => (
                      <option key={r.id} value={r.id}>
                        {tl(r.label)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  {t("Case reference")}
                  <input
                    type="text"
                    maxLength={60}
                    value={holdRef}
                    onChange={(e) => setHoldRef(e.target.value)}
                  />
                  {/* THE HINT IS THE CONTROL. There is no note field here and there
                      is not going to be one: what somebody would type about a named
                      person under investigation is exactly what must not be stored. */}
                  <span className="field-hint">
                    {t("A case or claim number only. Do not describe the matter or the person.")}
                  </span>
                </label>
                </div>
                <div className="row">
                <button
                  type="button"
                  className="btn"
                  disabled={
                    parseSubject(holdWorker) === null || holdReason === "" || holdRef.trim() === ""
                  }
                  onClick={() =>
                    void act(
                      privacyApi.openHold(apiBase, parseSubject(holdWorker)!, holdReason, holdRef.trim()),
                      t("The hold is open. This person cannot be erased until it is released or overridden."),
                    )
                  }
                >
                  {t("Open the hold")}
                </button>
                <button type="button" className="btn btn-quiet" onClick={() => setOpeningHold(false)}>
                  {t("Cancel")}
                </button>
                </div>
              </div>
            ) : null}
          </section>

</>
) },
            { id: "consent", label: t("Consent"), content: (
<>
          {/* ---- 3. CONSENT --------------------------------------------- */}
          <section className="panel">
            <h2>{t("Consent")}</h2>
            <p className="muted">
              {t("Only the things this product asks permission for. Anything needed to give somebody work is not consent-based and is not listed here — telling a person they may withdraw something they may not is worse than not offering it.")}
            </p>
            {data.consent.length === 0 ? (
              <p className="muted">
                {t("Nobody has made a decision either way. Nothing consent-based is being done with anybody’s data.")}
              </p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">{t("Person")}</th>
                    <th scope="col">{t("For")}</th>
                    <th scope="col">{t("Decision")}</th>
                    <th scope="col">{""}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.consent.map((c) => (
                    <tr key={c.id}>
                      <td>{c.worker_name}</td>
                      <td>
                        {tl(c.purpose_label)}
                        <br />
                        <span className="muted">{c.article}</span>
                      </td>
                      <td>
                        <StatusChip
                          tone={c.is_granted ? "live" : "neutral"}
                          label={c.is_granted ? t("Agreed") : t("Withdrawn")}
                        />
                        <br />
                        <span className="muted">
                          {whenIn(c.decided_at, orgZone)} ·{" "}
                          {c.decided_by_worker ? t("by them") : t("recorded for them")}
                          {c.changes > 0 ? ` · ${t("changed {n} time(s)", { n: c.changes })}` : ""}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-quiet"
                          onClick={() =>
                            void act(
                              privacyApi.recordConsent(
                                apiBase,
                                c.worker_id,
                                c.purpose,
                                !c.is_granted,
                              ),
                              c.is_granted
                                ? t("Withdrawal recorded. The earlier decision is kept as history.")
                                : t("Agreement recorded."),
                            )
                          }
                        >
                          {c.is_granted ? t("Record a withdrawal") : t("Record agreement")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

</>
) },
            { id: "erasures", label: t("Erasures carried out"), content: (
<>
          {/* ---- 4. WHAT WAS ERASED ------------------------------------- */}
          <section className="panel">
            <h2>{t("Erasures carried out")}</h2>
            <p className="muted">
              {t("These rows hold no name. They survive the erasure they record, which is what makes an erasure provable rather than merely done.")}
            </p>
            {data.erasures.length === 0 ? (
              <p className="muted">{t("Nobody has been erased.")}</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">{t("When")}</th>
                    <th scope="col">{t("Subject")}</th>
                    <th scope="col">{t("Rows")}</th>
                    <th scope="col">{t("Overrides")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.erasures.map((e) => (
                    <tr key={e.id}>
                      <td>{whenIn(e.performed_at, orgZone)}</td>
                      {/* ★ AN ERASURE THAT ONLY SEVERED THIS ORGANISATION'S LINK
                          MUST NOT READ AS ONE THAT CLOSED THE ACCOUNT. The row
                          carries three states and all three are rendered: closed,
                          left standing with the ground named, or no account to
                          reach at all. */}
                      <td>
                        {subjectKindLabel(data, e.subject_kind)}
                        {e.account_anonymised === true ? (
                          <>
                            <br />
                            <span className="muted">{t("Their sign-in was closed and unnamed")}</span>
                          </>
                        ) : e.account_anonymised === false ? (
                          <>
                            <br />
                            <StatusChip
                              tone="attention"
                              label={e.account_retained_label === null ? t("Their sign-in still stands") : tl(e.account_retained_label)}
                            />
                          </>
                        ) : null}
                      </td>
                      <td className="numeric">
                        {t("{rows} across {tables} table(s)", { rows: e.rows_erased, tables: e.relations_walked })}
                        {/* ★ WHAT WAS REFUSED, NEVER OMITTED — 0130/0132. These
                            rows are append-only evidence that a message was sent,
                            and they may only go when the whole tenant does. They
                            survive pointing at an account whose address now reads
                            erased-…@erased.invalid, so the record stands and the
                            person is not identifiable from it. A row showing only
                            the erased count reads as "everything went". */}
                        {e.rows_retained > 0 ? (
                          <>
                            <br />
                            <StatusChip
                              tone="attention"
                              label={t("{n} kept as evidence", { n: e.rows_retained })}
                            />
                            <br />
                            <span className="muted">
                              {e.retained_relations.map((rr) => rr.relation).join(", ")}
                            </span>
                          </>
                        ) : null}
                      </td>
                      <td>
                        {e.overrides_used === 0 ? (
                          <span className="muted">{t("None — no hold was live")}</span>
                        ) : (
                          <ul className="plain-list">
                            {e.overrides.map((o) => (
                              <li key={`${e.id}-${o.hold_reference}`}>
                                {tl(o.ground_label)} · {o.hold_reference}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

</>
) },
            { id: "retention", label: t("What we keep, and for how long"), content: (
<>
          {/* ---- 5. RETENTION ------------------------------------------- */}
          <section className="panel">
            <h2>{t("What we keep, and for how long")}</h2>
            {data.coverage_defects.length > 0 ? (
              <>
                <Notice tone="failed">
                  {t("{n} table(s) hold personal data with no retention decision", { n: data.coverage_defects.length })}
                </Notice>
                <ul className="plain-list">
                  {data.coverage_defects.map((d) => (
                    <li key={d.relation}>
                      <strong>{d.relation}</strong> {d.defect}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <Notice tone="live">
                {t("Every one of the {n} tables holding personal data has a retention decision", { n: data.retention.length })}
              </Notice>
            )}
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t("Record")}</th>
                  <th scope="col">{t("Kept for")}</th>
                  <th scope="col">{t("Then")}</th>
                  <th scope="col">{t("Why")}</th>
                </tr>
              </thead>
              <tbody>
                {data.retention.map((r) => (
                  <tr key={r.relation}>
                    <td>{r.relation}</td>
                    <td className="numeric">
                      {t("{n} year(s)", { n: Math.round(r.retention_days / 365) })}
                      <br />
                      <span className="muted">
                        {r.anchor_column !== null
                          ? t("from {column}", { column: r.anchor_column })
                          : t("with {relation}", { relation: r.retained_with ?? "" })}
                      </span>
                    </td>
                    <td>{tl(r.action_label)}</td>
                    <td>{r.basis}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

</>
) },
            { id: "working-time", label: t("Working-time limits"), content: (
<>
          {/* ---- 6. WORKING TIME ---------------------------------------- */}
          <section className="panel">
            <h2>{t("Working-time limits")}</h2>
            <p className="muted">
              {t("Applied by the scheduler on every booking. The row for this organisation’s country is the one that binds; the others are here because an agency working across a border needs to see what changes.")}
            </p>
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t("Country")}</th>
                  <th scope="col">{t("A day")}</th>
                  <th scope="col">{t("A week")}</th>
                  <th scope="col">{t("Rest")}</th>
                  <th scope="col">{t("In a row")}</th>
                  <th scope="col">{t("Where it comes from")}</th>
                </tr>
              </thead>
              <tbody>
                {data.working_time.map((l) => (
                  <tr key={l.country}>
                    <td>
                      {l.country}
                      {l.is_ours ? (
                        <>
                          <br />
                          <StatusChip tone="live" label={t("This organisation")} />
                        </>
                      ) : null}
                    </td>
                    <td className="numeric">{t("{n} h", { n: l.max_daily_hours })}</td>
                    <td className="numeric">
                      {t("{n} h", { n: l.max_weekly_hours })}
                      <br />
                      <span className="muted">{t("per calendar week, unless a working-time frame is recorded")}</span>
                    </td>
                    <td className="numeric">
                      {t("{n} h daily", { n: l.min_daily_rest_hours })}
                      <br />
                      <span className="muted">{t("{n} h weekly", { n: l.min_weekly_rest_hours })}</span>
                    </td>
                    <td className="numeric">{t("{n} days", { n: l.max_consecutive_days })}</td>
                    <td>{l.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
</>
) },
          ]}
        />
      ) : null}
    </div>
  );
}
